import { Color, Style } from "./alert-variants.ts";
import { FontStyles } from "../../utils/font-styles.ts";
import "../../../assets/fa-icons/css/fontawesome.css";
import "../../../assets/fa-icons/css/sharp-regular.css";
import { ReactElement } from "react";

interface AlertProps {
  title?: string;
  message: string;
  color: Color;
  style: Style;
  dismissible?: boolean;
  onDismiss?: () => void;
  renderLeadingIcon: () => ReactElement;
}

const baseStyle = "rounded flex gap-x-3.5";
const textContainer = "flex flex-col mr-4 max-w-md min-w-40";
const iconContainer = "flex justify-center items-center self-stretch";
const titleStyle = "font-semibold " + FontStyles["body-l"];
const messageStyle = "font-normal " + FontStyles["body-md"];
const solidStyles = "px-3 py-2.5 xs:px-4 xs:py-3";

const solidColorVariants: Record<Color, string> = {
  green: "text-neutral-white bg-glen",
  blue: "text-neutral-white bg-loch",
  white: "text-north-sea bg-neutral-white",
  amber: "text-neutral-white bg-autumn",
  red: "text-neutral-white bg-brick",
  gray: "text-neutral-white bg-light-slate",
  dark: "text-neutral-white bg-slate",
};

const outlineStyles = "border px-2.5 py-2 xs:px-3.5 xs:py-2.5 ";
const outlineColorVariants: Record<Color, string> = {
  green: "text-glen bg-glen-50 border-glen",
  blue: "text-loch bg-loch-50 border-loch",
  white: "text-north-sea bg-neutral-white border-north-sea",
  amber: "text-autumn bg-autumn-50 border-autumn",
  red: "text-brick bg-brick-50 border-brick",
  gray: "text-light-slate bg-light-cloud border-light-slate",
  dark: "text-slate bg-light-cloud border-slate",
};

export const Alert = ({
  title,
  message,
  color,
  style,
  dismissible = false,
  onDismiss,
  renderLeadingIcon,
}: AlertProps) => {
  const base_style: string[] = [baseStyle];
  const title_style: string[] = [titleStyle];
  const message_style: string[] = [messageStyle];

  switch (style) {
    case "solid":
      base_style.push(solidColorVariants[color]);
      base_style.push(solidStyles);
      break;
    case "outlined":
      base_style.push(outlineColorVariants[color]);
      base_style.push(outlineStyles);
  }
  const icon = renderLeadingIcon();
  return (
    <div className={base_style.join(" ")} role="alert">
      <div className={iconContainer}>{icon}</div>
      <div className={textContainer}>
        <span className={title_style.join(" ")}>{title}</span>
        <span className={message_style.join(" ")}>{message}</span>
      </div>
      {dismissible && (
        <div className={`${iconContainer} cursor-pointer`} onClick={() => onDismiss?.()}>
          <i className="fa-sharp fa-regular fa-xmark text-2xl" />
        </div>
      )}
    </div>
  );
};
